import * as i0 from '@angular/core';
import { Injectable, Directive, Input, NgModule } from '@angular/core';
import { map, startWith, BehaviorSubject, fromEvent, debounceTime, distinctUntilChanged } from 'rxjs';
import * as i1 from '@angular/cdk/layout';
import * as i1$1 from 'ngx-device-detector';
const EMPTY_ARRAY = [];
const WS_REGEXP = /\s+/;
class DigiBreakpointsMapper {
  constructor(data) {
    this.mappedData = [];
    this.mappedData = data.map(item => {
      if (item.min && item.max) {
        return `(min-width: ${item.min}px) and (max-width: ${item.max}px)`;
      } else if (item.min) {
        return `min-width: ${item.min}px`;
      } else if (item.max) {
        return `max-width: ${item.max}px`;
      }
      return '';
    });
  }
}
let LayoutService = /*#__PURE__*/(() => {
  class LayoutService {
    constructor(breakpointObserver) {
      this.breakpointObserver = breakpointObserver;
    }
    register(breakpoints) {
      let mappedData = [];
      if (Array.isArray(breakpoints) && typeof breakpoints[0] === 'string') {
        mappedData = breakpoints;
      } else if (Array.isArray(breakpoints)) {
        mappedData = new DigiBreakpointsMapper(breakpoints).mappedData;
      }
      return this.breakpointObserver.observe(mappedData).pipe(map(result => result.matches), startWith(this.breakpointObserver.isMatched(mappedData)));
    }
    static {
      this.ɵfac = function LayoutService_Factory(t) {
        return new (t || LayoutService)(i0.ɵɵinject(i1.BreakpointObserver));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LayoutService,
        factory: LayoutService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LayoutService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DeviceDetector = /*#__PURE__*/(() => {
  class DeviceDetector {
    constructor(deviceDetectorService) {
      this.deviceDetectorService = deviceDetectorService;
    }
    get isDesktop() {
      return this.deviceDetectorService.isDesktop();
    }
    get isMobile() {
      return this.deviceDetectorService.isMobile();
    }
    get isTablet() {
      return this.deviceDetectorService.isTablet();
    }
    static {
      this.ɵfac = function DeviceDetector_Factory(t) {
        return new (t || DeviceDetector)(i0.ɵɵinject(i1$1.DeviceDetectorService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DeviceDetector,
        factory: DeviceDetector.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DeviceDetector;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ScreenScaleService = /*#__PURE__*/(() => {
  class ScreenScaleService {
    constructor() {
      this.windowWidthSubject = new BehaviorSubject(window.innerWidth);
      this.windowHeightSubject = new BehaviorSubject(window.innerHeight);
      this.windowWidth$ = this.windowWidthSubject.asObservable();
      this.windowHeight$ = this.windowHeightSubject.asObservable();
      const resize$ = fromEvent(window, 'resize').pipe(debounceTime(200), map(() => ({
        width: window.innerWidth,
        height: window.innerHeight
      })), distinctUntilChanged((prev, curr) => prev.width === curr.width && prev.height === curr.height));
      resize$.subscribe(({
        width,
        height
      }) => {
        this.windowWidthSubject.next(width);
        this.windowHeightSubject.next(height);
      });
    }
    static {
      this.ɵfac = function ScreenScaleService_Factory(t) {
        return new (t || ScreenScaleService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ScreenScaleService,
        factory: ScreenScaleService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ScreenScaleService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const SERVICE = [LayoutService, DeviceDetector, ScreenScaleService];
let DigiClassDevice = /*#__PURE__*/(() => {
  class DigiClassDevice {
    constructor(ngEl, renderer, deviceDetector) {
      this.ngEl = ngEl;
      this.renderer = renderer;
      this.deviceDetector = deviceDetector;
      this.initialClasses = EMPTY_ARRAY;
      this.stateMap = new Map();
    }
    set klass(value) {
      this.initialClasses = value != null ? value.trim().split(WS_REGEXP) : EMPTY_ARRAY;
    }
    set digiClassDevice(value) {
      this.rawClass = typeof value === 'string' ? value.trim().split(WS_REGEXP) : value;
    }
    ngOnChanges() {
      for (const klass of this.initialClasses) {
        this.updateState(klass, true);
      }
      const rawClass = this.rawClass;
      if (Array.isArray(rawClass) || rawClass instanceof Set) {
        for (const klass of rawClass) {
          this.updateState(klass, true);
        }
      } else if (rawClass != null) {
        for (const klass of Object.keys(rawClass)) {
          let isMatched;
          switch (rawClass[klass]) {
            case 'desktop':
              isMatched = this.deviceDetector.isDesktop;
              break;
            case 'mobile':
              isMatched = this.deviceDetector.isMobile;
              break;
            case 'tablet':
              isMatched = this.deviceDetector.isTablet;
              break;
          }
          this.updateState(klass, isMatched);
        }
      }
      this.applyStateDiff();
    }
    updateState(klass, nextEnabled) {
      const state = this.stateMap.get(klass);
      if (state !== undefined) {
        if (state.enabled !== nextEnabled) {
          state.changed = true;
          state.enabled = nextEnabled;
        }
        state.touched = true;
      } else {
        this.stateMap.set(klass, {
          enabled: nextEnabled,
          changed: true,
          touched: true
        });
      }
    }
    applyStateDiff() {
      for (const stateEntry of this.stateMap) {
        const klass = stateEntry[0];
        const state = stateEntry[1];
        if (state.changed) {
          this.toggleClass(klass, state.enabled);
          state.changed = false;
        } else if (!state.touched) {
          // A class that was previously active got removed from the new collection of classes -
          // remove from the DOM as well.
          if (state.enabled) {
            this.toggleClass(klass, false);
          }
          this.stateMap.delete(klass);
        }
        state.touched = false;
      }
    }
    toggleClass(klass, enabled) {
      klass = klass.trim();
      if (klass.length > 0) {
        klass.split(WS_REGEXP).forEach(klass => {
          if (enabled) {
            this.renderer.addClass(this.ngEl.nativeElement, klass);
          } else {
            this.renderer.removeClass(this.ngEl.nativeElement, klass);
          }
        });
      }
    }
    static {
      this.ɵfac = function DigiClassDevice_Factory(t) {
        return new (t || DigiClassDevice)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DeviceDetector));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DigiClassDevice,
        selectors: [["", "digiClassDevice", ""]],
        inputs: {
          klass: [i0.ɵɵInputFlags.None, "class", "klass"],
          digiClassDevice: "digiClassDevice"
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return DigiClassDevice;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function assertTemplate(property, templateRef) {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${JSON.stringify(templateRef)}'.`);
  }
}
let DigiIfDevice = /*#__PURE__*/(() => {
  class DigiIfDevice {
    constructor(viewContainer, templateRef, deviceDetector) {
      this.viewContainer = viewContainer;
      this.deviceDetector = deviceDetector;
      this.context = new DigiIfDeviceContext();
      this.thenTemplateRef = null;
      this.elseTemplateRef = null;
      this.thenViewRef = null;
      this.elseViewRef = null;
      this.thenTemplateRef = templateRef;
    }
    /**
     * The Boolean expression to evaluate as the condition for showing a template.
     */
    set digiIfDevice(device) {
      let value;
      switch (device) {
        case 'desktop':
          value = this.deviceDetector.isDesktop;
          break;
        case "mobile":
          value = this.deviceDetector.isMobile;
          break;
        case 'tablet':
          value = this.deviceDetector.isTablet;
          break;
      }
      this.context.$implicit = this.context.digiIfDevice = value;
      this.updateView();
    }
    /**
     * A template to show if the condition expression evaluates to false.
     */
    set digiIfDeviceElse(templateRef) {
      assertTemplate('digiIfDeviceElse', templateRef);
      this.elseTemplateRef = templateRef;
      this.elseViewRef = null; // clear previous view if any.
      this.updateView();
    }
    updateView() {
      if (this.context.$implicit) {
        if (!this.thenViewRef) {
          this.viewContainer.clear();
          this.elseViewRef = null;
          if (this.thenTemplateRef) {
            this.thenViewRef = this.viewContainer.createEmbeddedView(this.thenTemplateRef, this.context);
          }
        }
      } else {
        if (!this.elseViewRef) {
          this.viewContainer.clear();
          this.thenViewRef = null;
          if (this.elseTemplateRef) {
            this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef, this.context);
          }
        }
      }
    }
    static {
      this.ɵfac = function DigiIfDevice_Factory(t) {
        return new (t || DigiIfDevice)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.TemplateRef), i0.ɵɵdirectiveInject(DeviceDetector));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DigiIfDevice,
        selectors: [["", "digiIfDevice", ""]],
        inputs: {
          digiIfDevice: "digiIfDevice",
          digiIfDeviceElse: "digiIfDeviceElse"
        }
      });
    }
  }
  return DigiIfDevice;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class DigiIfDeviceContext {
  constructor() {
    this.$implicit = null;
    this.digiIfDevice = null;
  }
}
let DigiIfMedia = /*#__PURE__*/(() => {
  class DigiIfMedia {
    constructor(viewContainer, templateRef, layoutService) {
      this.viewContainer = viewContainer;
      this.layoutService = layoutService;
      this.context = new DigiIfMediaContext();
      this.thenTemplateRef = null;
      this.elseTemplateRef = null;
      this.thenViewRef = null;
      this.elseViewRef = null;
      this.thenTemplateRef = templateRef;
    }
    /**
     * The Boolean expression to evaluate as the condition for showing a template.
     */
    set digiIfMedia(breakpoints) {
      this.registerSubscription = this.layoutService.register(breakpoints).subscribe({
        next: value => {
          this.context.$implicit = this.context.digiIfMedia = value;
          this.updateView();
        }
      });
    }
    /**
     * A template to show if the condition expression evaluates to false.
     */
    set digiIfMediaElse(templateRef) {
      assertTemplate('digiIfMediaElse', templateRef);
      this.elseTemplateRef = templateRef;
      this.elseViewRef = null; // clear previous view if any.
      this.updateView();
    }
    updateView() {
      if (this.context.$implicit) {
        if (!this.thenViewRef) {
          this.viewContainer.clear();
          this.elseViewRef = null;
          if (this.thenTemplateRef) {
            this.thenViewRef = this.viewContainer.createEmbeddedView(this.thenTemplateRef, this.context);
          }
        }
      } else {
        if (!this.elseViewRef) {
          this.viewContainer.clear();
          this.thenViewRef = null;
          if (this.elseTemplateRef) {
            this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef, this.context);
          }
        }
      }
    }
    ngOnDestroy() {
      this.registerSubscription.unsubscribe();
    }
    static {
      this.ɵfac = function DigiIfMedia_Factory(t) {
        return new (t || DigiIfMedia)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.TemplateRef), i0.ɵɵdirectiveInject(LayoutService));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DigiIfMedia,
        selectors: [["", "digiIfMedia", ""]],
        inputs: {
          digiIfMedia: "digiIfMedia",
          digiIfMediaElse: "digiIfMediaElse"
        }
      });
    }
  }
  return DigiIfMedia;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class DigiIfMediaContext {
  constructor() {
    this.$implicit = null;
    this.digiIfMedia = null;
  }
}
let DigiClassMedia = /*#__PURE__*/(() => {
  class DigiClassMedia {
    constructor(ngEl, renderer, layoutService) {
      this.ngEl = ngEl;
      this.renderer = renderer;
      this.layoutService = layoutService;
      this.initialClasses = EMPTY_ARRAY;
      this.stateMap = new Map();
    }
    set klass(value) {
      this.initialClasses = value != null ? value.trim().split(WS_REGEXP) : EMPTY_ARRAY;
    }
    set digiClassMedia(value) {
      this.rawClass = typeof value === 'string' ? value.trim().split(WS_REGEXP) : value;
    }
    ngOnChanges() {
      for (const klass of this.initialClasses) {
        this.updateState(klass, true);
      }
      const rawClass = this.rawClass;
      if (Array.isArray(rawClass) || rawClass instanceof Set) {
        for (const klass of rawClass) {
          this.updateState(klass, true);
        }
      } else if (rawClass != null) {
        for (const klass of Object.keys(rawClass)) {
          this.layoutSubscription = this.layoutService.register(rawClass[klass]).subscribe({
            next: isMatched => {
              this.updateState(klass, isMatched);
            }
          });
        }
      }
      this.applyStateDiff();
    }
    updateState(klass, nextEnabled) {
      const state = this.stateMap.get(klass);
      if (state !== undefined) {
        if (state.enabled !== nextEnabled) {
          state.changed = true;
          state.enabled = nextEnabled;
        }
        state.touched = true;
      } else {
        this.stateMap.set(klass, {
          enabled: nextEnabled,
          changed: true,
          touched: true
        });
      }
    }
    applyStateDiff() {
      for (const stateEntry of this.stateMap) {
        const klass = stateEntry[0];
        const state = stateEntry[1];
        if (state.changed) {
          this.toggleClass(klass, state.enabled);
          state.changed = false;
        } else if (!state.touched) {
          // A class that was previously active got removed from the new collection of classes -
          // remove from the DOM as well.
          if (state.enabled) {
            this.toggleClass(klass, false);
          }
          this.stateMap.delete(klass);
        }
        state.touched = false;
      }
    }
    toggleClass(klass, enabled) {
      klass = klass.trim();
      if (klass.length > 0) {
        klass.split(WS_REGEXP).forEach(klass => {
          if (enabled) {
            this.renderer.addClass(this.ngEl.nativeElement, klass);
          } else {
            this.renderer.removeClass(this.ngEl.nativeElement, klass);
          }
        });
      }
      this.dispose();
    }
    dispose() {
      this.layoutSubscription.unsubscribe();
    }
    static {
      this.ɵfac = function DigiClassMedia_Factory(t) {
        return new (t || DigiClassMedia)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(LayoutService));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DigiClassMedia,
        selectors: [["", "digiClassMedia", ""]],
        inputs: {
          klass: [i0.ɵɵInputFlags.None, "class", "klass"],
          digiClassMedia: "digiClassMedia"
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return DigiClassMedia;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DIRECTIVES = [DigiClassDevice, DigiClassMedia, DigiIfDevice, DigiIfMedia];
let DigipayLayoutModule = /*#__PURE__*/(() => {
  class DigipayLayoutModule {
    static {
      this.ɵfac = function DigipayLayoutModule_Factory(t) {
        return new (t || DigipayLayoutModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: DigipayLayoutModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [SERVICE]
      });
    }
  }
  return DigipayLayoutModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DIGI_BREAKPOINTS = {
  Small: '(min-width: 0px) and (max-width: 599px)',
  Medium: '(min-width: 600px) and (max-width: 1023px)',
  Large: '(min-width: 1024px) and (max-width: 1439px)',
  XLarge: '(min-width: 1440px)'
};

/*
 * Public API Surface of digipay-layout
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DIGI_BREAKPOINTS, DIRECTIVES, DeviceDetector, DigiClassDevice, DigiClassMedia, DigiIfDevice, DigiIfDeviceContext, DigiIfMedia, DigiIfMediaContext, DigipayLayoutModule, LayoutService, SERVICE, ScreenScaleService };
